import React from "react";

type MetricTone = "primary" | "secondary" | "accent" | "amber" | "rose" | "slate";

export interface MetricCardProps {
  label: string;
  value: React.ReactNode;
  helper?: React.ReactNode;
  icon?: React.ReactNode;
  tone?: MetricTone;
  className?: string;
}

const valueToneClass: Record<MetricTone, string> = {
  primary: "text-primary-700",
  secondary: "text-secondary-700",
  accent: "text-accent-700",
  amber: "text-amber-600",
  rose: "text-rose-600",
  slate: "text-slate-950",
};

const iconToneClass: Record<MetricTone, string> = {
  primary: "border-primary-100 bg-primary-50 text-primary-700",
  secondary: "border-secondary-100 bg-secondary-50 text-secondary-700",
  accent: "border-accent-100 bg-accent-50 text-accent-700",
  amber: "border-amber-100 bg-amber-50 text-amber-600",
  rose: "border-rose-100 bg-rose-50 text-rose-600",
  slate: "border-slate-200 bg-slate-50 text-slate-600",
};

export function MetricCard({
  label,
  value,
  helper,
  icon,
  tone = "primary",
  className = "",
}: MetricCardProps) {
  return (
    <article
      className={`relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm shadow-slate-200/70 md:p-5 ${className}`}
    >
      <div className={icon ? "pr-12 md:pr-16" : ""}>
        <p className="text-[10px] font-black uppercase leading-5 tracking-[0.12em] text-slate-500 md:text-[11px] md:tracking-[0.16em]">
          {label}
        </p>
        <p
          className={`mt-3 min-w-0 truncate text-2xl font-black tracking-tight md:text-3xl ${valueToneClass[tone]}`}
          title={typeof value === "string" ? value : undefined}
        >
          {value}
        </p>
        {helper ? (
          <p className="mt-1 min-w-0 truncate text-xs font-medium text-slate-500 md:text-sm">
            {helper}
          </p>
        ) : null}
      </div>
      {icon ? (
        <span
          className={`absolute right-4 top-1/2 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-2xl border text-xl md:h-14 md:w-14 md:text-2xl ${iconToneClass[tone]}`}
          aria-hidden="true"
        >
          {icon}
        </span>
      ) : null}
    </article>
  );
}
